import { ref, computed, h } from "vue";
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	getPaginationRowModel,
	useVueTable,
} from "@tanstack/vue-table";
import dayjs from "dayjs";
import { getBookingTypeLabel } from "../../calendar/utils/bookingTypeLabel.js";

const columnHelper = createColumnHelper();

export function useBookingsTable(
	bookingsGetter,
	actions = {},
	options = {},
	masterData = null
) {
	const sorting = ref([{ id: "datetime", desc: false }]);
	const pagination = ref({
		pageIndex: 0,
		pageSize: 10,
	});
	const columnVisibility = ref({});
	const columnSizing = ref({});

	const { showPointsColumn = ref(false), showTypeColumn = ref(false) } = options;

	const baseColumns = [
		// Date & Time column (1st position)
		columnHelper.accessor("startTime", {
			id: "datetime",
			header: "Termin",
			size: 180,
			minSize: 160,
			maxSize: 200,
			cell: ({ row }) => {
				const { startTime, endTime } = row.original;
				if (!startTime || !endTime) return "-";

				const start = dayjs(startTime);
				const end = dayjs(endTime);

				return h("div", [
					h("div", `${start.format("ddd")}, ${start.format("DD.MM.YYYY")}`),
					h(
						"div",
						{ class: "text-muted" },
						`${start.format("HH:mm")} - ${end.format("HH:mm")}`
					),
				]);
			},
			sortingFn: "datetime",
		}),
	];

	// Conditionally add Points column for students
	const pointsColumn = columnHelper.accessor("points", {
		header: "Punkte",
		size: 80,
		minSize: 70,
		maxSize: 90,
		cell: ({ getValue }) => {
			const points = getValue();
			return points ? `${points}` : "-";
		},
		sortingFn: "basic",
	});

	// Booking type column - makes Punktebuchung / Buchung ohne Punkte / Sofortbuchung
	// distinguishable in the list, not only in the details view.
	const typeColumn = columnHelper.accessor((row) => getBookingTypeLabel(row), {
		id: "bookingType",
		header: "Buchungstyp",
		size: 140,
		minSize: 120,
		maxSize: 160,
		cell: ({ getValue }) => getValue(),
		sortingFn: "alphanumeric",
	});

	const otherColumns = [
		// Room column
		columnHelper.accessor("room", {
			id: "room",
			header: "Raum",
			size: 85,
			minSize: 80,
			maxSize: 90,
			cell: ({ getValue }) => {
				const room = getValue();
				return `${room.buildingShort} ${room.doorLabel}`;
			},
			enableSorting: false,
		}),

		// Title column
		columnHelper.accessor("title", {
			header: "Titel",
			size: 150,
			minSize: 100,
			maxSize: 200,
			cell: ({ getValue }) => {
				const title = getValue();
				return h("span", { class: "" }, title || "-");
			},
		}),

		// Duration column
		columnHelper.accessor(
			(row) => {
				const { startTime, endTime } = row;
				if (!startTime || !endTime) return 0;
				return dayjs(endTime).diff(dayjs(startTime), "minute");
			},
			{
				id: "duration",
				header: "Dauer",
				size: 90,
				minSize: 80,
				maxSize: 100,
				cell: ({ getValue }) => {
					const durationMinutes = getValue();
					if (!durationMinutes || durationMinutes === 0) return "-";

					const hours = Math.floor(durationMinutes / 60);
					const minutes = durationMinutes % 60;
					const durationText =
						hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;

					return h("span", { class: "text-muted" }, durationText);
				},
				sortingFn: "basic",
			}
		),

		// Actions column
		columnHelper.display({
			id: "actions",
			header: "",
			size: 80,
			minSize: 80,
			maxSize: 80,
			enableHiding: false,
			enableResizing: false,
			cell: ({ row }) => {
				const booking = row.original;
				const canEdit = booking.permissions?.canUpdate ?? true;
				const canDelete = booking.permissions?.canDelete ?? true;

				const buttons = [];

				if (canEdit) {
					buttons.push(
						h(
							"button",
							{
								class: "btn btn-outline-primary btn-sm",
								title: "Ändern",
								onClick: () => actions.onEditBooking?.(booking),
							},
							h("i", { class: "fas fa-edit" })
						)
					);
				}

				if (canDelete) {
					buttons.push(
						h(
							"button",
							{
								class: "btn btn-outline-danger btn-sm",
								title: "Stornieren",
								onClick: () => actions.onCancelBooking?.(booking),
							},
							h("i", { class: "fas fa-trash" })
						)
					);
				}

				if (booking.cancelledAt) {
					return h(
						"span",
						{
							class: "cancelled-badge"
						},
						"Storniert"
					)
				}

				return h("div", { class: "d-flex gap-1" }, buttons);
			},
		}),
	];

	// Build final columns array based on options (reactive)
	const columns = computed(() => [
		...baseColumns,
		...(showPointsColumn.value ? [pointsColumn] : []),
		...(showTypeColumn.value ? [typeColumn] : []),
		...otherColumns,
	]);

	const data = computed(() => {
		const bookings =
			typeof bookingsGetter === "function" ? bookingsGetter() : bookingsGetter;
		return bookings || [];
	});

	const table = useVueTable({
		get data() {
			return data.value;
		},
		get columns() {
			return columns.value;
		},
		state: {
			get sorting() {
				return sorting.value;
			},
			get pagination() {
				return pagination.value;
			},
			get columnVisibility() {
				return columnVisibility.value;
			},
			get columnSizing() {
				return columnSizing.value;
			},
		},
		onSortingChange: (updaterOrValue) => {
			sorting.value =
				typeof updaterOrValue === "function"
					? updaterOrValue(sorting.value)
					: updaterOrValue;
		},
		onPaginationChange: (updaterOrValue) => {
			pagination.value =
				typeof updaterOrValue === "function"
					? updaterOrValue(pagination.value)
					: updaterOrValue;
		},
		onColumnVisibilityChange: (updaterOrValue) => {
			columnVisibility.value =
				typeof updaterOrValue === "function"
					? updaterOrValue(columnVisibility.value)
					: updaterOrValue;
		},
		onColumnSizingChange: (updaterOrValue) => {
			columnSizing.value =
				typeof updaterOrValue === "function"
					? updaterOrValue(columnSizing.value)
					: updaterOrValue;
		},
		enableColumnResizing: false, // Disable resizing for consistent layout
		columnResizeMode: "onEnd",
		defaultColumn: {
			minSize: 50,
			maxSize: 500,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	return {
		table,
		sorting,
		pagination,
		columnVisibility,
		columnSizing,
	};
}
